import React, { useState, useEffect } from 'react';
import { Layers, Download, Loader2, ArrowLeft, X, GripVertical, Edit2 } from 'lucide-react';
import { Link } from 'react-router-dom';
import { mergePDFs } from '../utils/pdfTools';
import { generatePdfThumbnail } from '../utils/pdfToImage';

const MergePdf = () => {
    const [files, setFiles] = useState([]);
    const [isProcessing, setIsProcessing] = useState(false);
    const [draggedIndex, setDraggedIndex] = useState(null);
    const [outputFileName, setOutputFileName] = useState('merged.pdf');
    const [isEditingFilename, setIsEditingFilename] = useState(false);

    // Generate smart filename when files change
    useEffect(() => {
        if (files.length > 0 && !isEditingFilename) {
            const firstName = files[0].file.name.replace('.pdf', '');
            const newName = files.length === 1 ? `${firstName}.pdf` : `${firstName}_merged.pdf`;
            setOutputFileName(newName);
        }
    }, [files, isEditingFilename]);

    const handleFileChange = async (e) => {
        const newFiles = Array.from(e.target.files).filter(file => file.type === 'application/pdf');
        await addFilesWithPreviews(newFiles);
    };

    const handleDrop = async (e) => {
        e.preventDefault();
        const newFiles = Array.from(e.dataTransfer.files).filter(file => file.type === 'application/pdf');
        await addFilesWithPreviews(newFiles);
    };

    const handleDragOver = (e) => {
        e.preventDefault();
    };

    const addFilesWithPreviews = async (newFiles) => {
        // Add files with preview generation
        const filesWithPreviews = await Promise.all(
            newFiles.map(async (file) => {
                try {
                    const preview = await generatePdfThumbnail(file, 120);
                    return { file, preview };
                } catch (error) {
                    console.error('Error generating preview:', error);
                    return { file, preview: null };
                }
            })
        );
        setFiles(prev => [...prev, ...filesWithPreviews]);
    };

    const removeFile = (index) => {
        setFiles(prev => prev.filter((_, i) => i !== index));
    };

    // Drag and drop handlers for reordering
    const handleDragStart = (e, index) => {
        setDraggedIndex(index);
        e.dataTransfer.effectAllowed = 'move';
    };

    const handleDragEnter = (e, index) => {
        e.preventDefault();
        if (draggedIndex === null || draggedIndex === index) return;

        const newFiles = [...files];
        const draggedFile = newFiles[draggedIndex];
        newFiles.splice(draggedIndex, 1);
        newFiles.splice(index, 0, draggedFile);

        setFiles(newFiles);
        setDraggedIndex(index);
    };

    const handleDragEnd = () => {
        setDraggedIndex(null);
    };

    const handleMerge = async () => {
        if (files.length < 2) return;

        setIsProcessing(true);
        try {
            const fileObjects = files.map(f => f.file);
            const mergedBlob = await mergePDFs(fileObjects);
            const url = URL.createObjectURL(mergedBlob);
            const link = document.createElement('a');
            link.href = url;
            link.download = outputFileName;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        } catch (error) {
            console.error('Error merging PDFs:', error);
            alert('Failed to merge PDFs. Please try again.');
        } finally {
            setIsProcessing(false);
        }
    };

    return (
        <div className="container animate-fade-in">
            <div style={{ marginBottom: '2rem' }}>
                <Link to="/tools/pdf" className="btn btn-secondary" style={{ display: 'inline-flex', alignItems: 'center', gap: '0.5rem', textDecoration: 'none' }}>
                    <ArrowLeft size={16} /> Back to PDF Tools
                </Link>
            </div>

            <header className="flex-center" style={{ flexDirection: 'column', marginBottom: '3rem' }}>
                <div className="flex-center" style={{
                    width: '64px',
                    height: '64px',
                    background: 'linear-gradient(135deg, var(--primary), var(--primary-hover))',
                    borderRadius: '1rem',
                    marginBottom: '1rem',
                    color: 'white',
                    boxShadow: '0 10px 25px -5px rgba(99, 102, 241, 0.4)'
                }}>
                    <Layers size={32} />
                </div>
                <h1>Merge PDF</h1>
                <p>Combine multiple PDF files into one single document</p>
            </header>

            <main style={{ maxWidth: '900px', margin: '0 auto' }}>
                <div
                    className="glass-card"
                    style={{ marginBottom: '2rem', textAlign: 'center', borderStyle: 'dashed', borderWidth: '2px' }}
                    onDrop={handleDrop}
                    onDragOver={handleDragOver}
                >
                    <input
                        type="file"
                        id="file-upload"
                        multiple
                        accept=".pdf"
                        onChange={handleFileChange}
                        style={{ display: 'none' }}
                    />
                    <label htmlFor="file-upload" className="btn btn-secondary" style={{ cursor: 'pointer' }}>
                        Select PDF Files
                    </label>
                    <p style={{ marginTop: '1rem', fontSize: '0.9rem', color: 'var(--text-muted)' }}>or drag and drop files here</p>
                </div>

                {files.length > 0 && (
                    <>
                        <div className="glass-card" style={{ marginBottom: '2rem' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
                                <h3>Selected Files ({files.length})</h3>
                                <p style={{ fontSize: '0.9rem', color: 'var(--text-muted)' }}>
                                    <GripVertical size={16} style={{ display: 'inline', verticalAlign: 'middle' }} /> Drag to reorder
                                </p>
                            </div>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                                {files.map((fileObj, index) => (
                                    <div
                                        key={index}
                                        draggable
                                        onDragStart={(e) => handleDragStart(e, index)}
                                        onDragEnter={(e) => handleDragEnter(e, index)}
                                        onDragEnd={handleDragEnd}
                                        style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '1rem',
                                            padding: '1rem',
                                            background: draggedIndex === index ? 'rgba(99, 102, 241, 0.1)' : 'rgba(255, 255, 255, 0.6)',
                                            borderRadius: '0.75rem',
                                            border: '2px solid var(--border)',
                                            cursor: 'move',
                                            transition: 'all 0.2s ease',
                                            opacity: draggedIndex === index ? 0.5 : 1
                                        }}
                                    >
                                        <div style={{ color: 'var(--text-muted)', cursor: 'grab' }}>
                                            <GripVertical size={20} />
                                        </div>

                                        {fileObj.preview && (
                                            <div style={{
                                                width: '80px',
                                                height: '100px',
                                                borderRadius: '0.5rem',
                                                overflow: 'hidden',
                                                border: '2px solid var(--border)',
                                                flexShrink: 0,
                                                background: 'white'
                                            }}>
                                                <img
                                                    src={fileObj.preview}
                                                    alt="PDF preview"
                                                    style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                                />
                                            </div>
                                        )}

                                        <div style={{ flex: 1, overflow: 'hidden', minWidth: 0 }}>
                                            <div style={{
                                                fontWeight: 600,
                                                marginBottom: '0.25rem',
                                                whiteSpace: 'nowrap',
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis'
                                            }}>
                                                {fileObj.file.name}
                                            </div>
                                            <div style={{ fontSize: '0.85rem', color: 'var(--text-muted)' }}>
                                                {(fileObj.file.size / 1024 / 1024).toFixed(2)} MB
                                            </div>
                                        </div>

                                        <div style={{
                                            background: 'rgba(99, 102, 241, 0.1)',
                                            color: 'var(--primary)',
                                            padding: '0.5rem 1rem',
                                            borderRadius: '0.5rem',
                                            fontWeight: 600,
                                            fontSize: '0.9rem'
                                        }}>
                                            #{index + 1}
                                        </div>

                                        <button
                                            onClick={() => removeFile(index)}
                                            style={{
                                                background: 'rgba(239, 68, 68, 0.1)',
                                                border: 'none',
                                                cursor: 'pointer',
                                                color: '#ef4444',
                                                padding: '0.5rem',
                                                borderRadius: '0.5rem',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                transition: 'all 0.2s ease'
                                            }}
                                            onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(239, 68, 68, 0.2)'}
                                            onMouseLeave={(e) => e.currentTarget.style.background = 'rgba(239, 68, 68, 0.1)'}
                                        >
                                            <X size={20} />
                                        </button>
                                    </div>
                                ))}
                            </div>
                        </div>

                        {files.length >= 2 && (
                            <div className="glass-card animate-fade-in" style={{ marginBottom: '2rem' }}>
                                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 600 }}>
                                    Output Filename
                                </label>
                                <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                                    <input
                                        type="text"
                                        value={outputFileName}
                                        onChange={(e) => {
                                            setOutputFileName(e.target.value);
                                            setIsEditingFilename(true);
                                        }}
                                        style={{
                                            flex: 1,
                                            padding: '0.75rem 1rem',
                                            borderRadius: '0.5rem',
                                            border: '2px solid var(--border)',
                                            fontSize: '1rem',
                                            background: 'rgba(255, 255, 255, 0.6)'
                                        }}
                                        placeholder="merged.pdf"
                                    />
                                    <Edit2 size={20} style={{ color: 'var(--text-muted)' }} />
                                </div>
                            </div>
                        )}
                    </>
                )}

                {files.length >= 2 && (
                    <div className="flex-center mt-4 mb-8 animate-fade-in">
                        <button
                            className="btn btn-primary"
                            onClick={handleMerge}
                            disabled={isProcessing}
                            style={{ padding: '1rem 2.5rem', fontSize: '1.1rem' }}
                        >
                            {isProcessing ? (
                                <>
                                    <Loader2 className="animate-spin" /> Merging PDFs...
                                </>
                            ) : (
                                <>
                                    <Download /> Merge {files.length} PDFs
                                </>
                            )}
                        </button>
                    </div>
                )}
            </main>
        </div>
    );
};

export default MergePdf;
